import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '../api';
import { confirmSignUp, resendSignUpCode, signIn, signUp } from '../auth';
import { Logo } from '../components/Logo';

const PROFILE_KEY = 'splitline.signupProfile';

interface Profile {
  name: string;
  organization: string;
  expectedRunners: number | null;
  referral: string;
}

/** Save what they told us at sign-up once they're signed in (the API needs a token). */
export async function flushSignupProfile(): Promise<void> {
  let profile: Profile | null = null;
  try {
    profile = JSON.parse(localStorage.getItem(PROFILE_KEY) ?? 'null');
  } catch {
    profile = null;
  }
  if (!profile?.name) return;
  await api('/me/profile', { method: 'PUT', body: profile });
  localStorage.removeItem(PROFILE_KEY);
}

/** Self-service sign-up for race directors: free while in beta. */
export function SignUpPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState<'details' | 'code'>(params.get('confirm') ? 'code' : 'details');
  const [f, setF] = useState({ name: '', organization: '', email: params.get('confirm') ?? '', password: '', expectedRunners: '', referral: '', agree: false });
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submitDetails = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const profile: Profile = { name: f.name.trim(), organization: f.organization.trim(), expectedRunners: f.expectedRunners ? Number(f.expectedRunners) : null, referral: f.referral.trim() };
      try {
        localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
      } catch {
        /* the profile can be filled in later */
      }
      try {
        await signUp(f.email.trim(), f.password);
      } catch (err) {
        if ((err as { code?: string }).code !== 'UsernameExistsException') throw err;
        throw new Error('There’s already an account for this email. Sign in instead, or use “Forgot password?” on the sign-in page.');
      }
      setStep('code');
      setInfo(`We sent a code to ${f.email.trim()}. It can take a minute; check spam too.`);
    });
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await confirmSignUp(f.email.trim(), code.trim());
      if (!f.password) {
        // Came back to confirm later: sign in normally.
        navigate('/login', { replace: true });
        return;
      }
      await signIn(f.email.trim(), f.password);
      await flushSignupProfile().catch(() => undefined);
      navigate('/?welcome=1', { replace: true });
    });
  };

  return (
    <div className="center-page">
      {step === 'details' ? (
        <form className="card center-card stack" onSubmit={submitDetails} style={{ maxWidth: 480 }}>
          <Link to="/" className="inline" style={{ gap: 10, color: 'inherit', textDecoration: 'none' }}>
            <Logo />
            <span className="display" style={{ fontSize: 26 }}>
              Splitline
            </span>
          </Link>
          <h1 className="display" style={{ fontSize: 22 }}>
            Create your free account
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            For race directors and timing volunteers. Free while Splitline is in beta, for races of up to 1,000 runners.
          </p>
          <label className="field">
            Your name
            <input className="input" autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
          </label>
          <label className="field">
            Club, race or organisation
            <input className="input" autoComplete="organization" value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} placeholder="Riverside Running Club" />
          </label>
          <label className="field">
            Email
            <input className="input" type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          </label>
          <label className="field">
            Password
            <input className="input" type="password" autoComplete="new-password" minLength={12} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
            <span className="hint">At least 12 characters, with upper and lower case letters, a number and a symbol.</span>
          </label>
          <div className="form-grid">
            <label className="field">
              Runners in your next race
              <input className="input" type="number" min={1} inputMode="numeric" value={f.expectedRunners} onChange={(e) => setF({ ...f, expectedRunners: e.target.value })} placeholder="About how many?" />
            </label>
            <label className="field">
              How did you hear about us?
              <input className="input" value={f.referral} onChange={(e) => setF({ ...f, referral: e.target.value })} />
            </label>
          </div>
          <label className="check" style={{ alignItems: 'flex-start' }}>
            <input type="checkbox" checked={f.agree} onChange={(e) => setF({ ...f, agree: e.target.checked })} required />
            <span className="small">
              I agree to the <Link to="/docs/terms">terms of use</Link> and <Link to="/docs/privacy-policy">privacy policy</Link>, and I’ll tell runners their race is filmed for timing.
            </span>
          </label>
          {error && <div className="notice bad">{error}</div>}
          <button className="btn primary" type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create account'}
          </button>
          <p className="small muted" style={{ margin: 0 }}>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      ) : (
        <form className="card center-card stack" onSubmit={submitCode}>
          <div className="inline" style={{ gap: 10 }}>
            <Logo />
            <span className="display" style={{ fontSize: 26 }}>
              Splitline
            </span>
          </div>
          <h1 className="display" style={{ fontSize: 22 }}>
            Check your email
          </h1>
          {info && <div className="notice info">{info}</div>}
          <label className="field">
            Email
            <input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          </label>
          <label className="field">
            Code
            <input className="input mono" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />
          </label>
          {error && <div className="notice bad">{error}</div>}
          <button className="btn primary" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Confirm and continue'}
          </button>
          <button
            type="button"
            className="btn link"
            disabled={busy || !f.email}
            onClick={() => void run(async () => {
              await resendSignUpCode(f.email.trim());
              setInfo(`Sent a new code to ${f.email.trim()}.`);
            })}
          >
            Send a new code
          </button>
        </form>
      )}
    </div>
  );
}
